import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@neo/primitives/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { useEffect, useState } from 'react'
import { VoiceType } from 'v2/pages/flow/types'
import { errorToast, generateToastsForMutation } from 'v2/utils/toasts'
import type {
  CommunityVoicesFormValues,
  CustomVoiceModalProps,
  CustomVoiceTab,
  ProfessionalVoicesFormValues,
  VoiceCloneFormValues,
} from './custom-voice.types'
import { useCloneVoiceMutation } from './queries'
import { CommunityVoicesTab } from './tabs/community-voices-tab'
import { ProfessionalVoicesTab } from './tabs/professional-voices-tab'
import { VoiceCloneTab } from './tabs/voice-clone-tab'

export const CustomVoiceModal = ({ open, onOpenChange }: CustomVoiceModalProps) => {
  const [activeTab, setActiveTab] = useState<CustomVoiceTab>('voice-clone')

  const cloneVoiceMutation = useCloneVoiceMutation()

  useEffect(() => {
    if (!open) setActiveTab('voice-clone')
  }, [open])

  const handleClose = () => {
    onOpenChange(false)
  }

  const handleSubmit = (formData: FormData) => {
    cloneVoiceMutation.mutate(
      formData,
      generateToastsForMutation(
        {
          title: 'Voice cloned successfully',
          description: 'You can now use the cloned voice in your flow.',
          callback: () => onOpenChange(false),
        },
        {
          title: 'Failed to clone voice',
          description: 'An error occurred while cloning the voice.',
        },
      ),
    )
  }

  const handleVoiceCloneSubmit = (values: VoiceCloneFormValues) => {
    if (!values.audioFile) {
      errorToast({ title: 'Please upload an audio file' })
      return
    }

    const formData = new FormData()
    formData.append('type', VoiceType.INSTANT)
    formData.append('label', values.voiceName)
    formData.append('description', values.description)
    formData.append('gender', values.gender)
    formData.append('files', values.audioFile)

    handleSubmit(formData)
  }

  const handleCommunitySubmit = (values: CommunityVoicesFormValues) => {
    if (!values.selectedVoice) {
      errorToast({ title: 'Please select a voice' })
      return
    }

    const formData = new FormData()
    formData.append('type', VoiceType.COMMUNITY)
    formData.append('voice_id', values.selectedVoice.voice_id)
    formData.append('voice_name', values.selectedVoice.name)
    formData.append('public_owner_id', values.selectedVoice.public_owner_id)

    handleSubmit(formData)
  }

  const handleProfessionalSubmit = (values: ProfessionalVoicesFormValues) => {
    const trimmedUrl = values.shareLinkUrl.trim()
    if (!trimmedUrl) {
      errorToast({ title: 'Please enter a share link URL' })
      return
    }

    const formData = new FormData()
    formData.append('type', VoiceType.PROFESSIONAL)
    formData.append('voice_url', trimmedUrl)

    handleSubmit(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border border-border bg-card shadow-bs1 sm:max-w-[500px]"
        data-testid="custom-voice-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-h4-500">Add custom voice</DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as CustomVoiceTab)}
          className="overflow-hidden w-full"
        >
          <TabsList variant="simple" className="w-full">
            <TabsTrigger value="voice-clone" data-testid="tab-voice-clone">
              Voice clone
            </TabsTrigger>
            <TabsTrigger value="community-voices" data-testid="tab-community-voices">
              Community voices
            </TabsTrigger>
            <TabsTrigger value="professional-voices" data-testid="tab-professional-voices">
              Professional voices
            </TabsTrigger>
          </TabsList>

          <div className="px-1">
            <TabsContent value="voice-clone">
              <VoiceCloneTab
                onCancel={handleClose}
                onSubmit={handleVoiceCloneSubmit}
                isSubmitting={cloneVoiceMutation.isPending}
              />
            </TabsContent>

            <TabsContent value="community-voices">
              <CommunityVoicesTab
                onCancel={handleClose}
                onSubmit={handleCommunitySubmit}
                isSubmitting={cloneVoiceMutation.isPending}
              />
            </TabsContent>

            <TabsContent value="professional-voices">
              <ProfessionalVoicesTab
                onCancel={handleClose}
                onSubmit={handleProfessionalSubmit}
                isSubmitting={cloneVoiceMutation.isPending}
              />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
